/**
 * Decorative countdown shown in the cart drawer. Purely psychological
 * urgency — does not reserve stock or clear the cart. The end timestamp
 * is persisted in sessionStorage so a page reload continues the same
 * countdown instead of resetting it. Once it reaches zero it just starts
 * a fresh countdown from the top, indefinitely — data-expired-text is
 * kept as an attribute for backward compat but is never shown.
 *
 * Usage:
 *   <cart-expiry-timer
 *     data-active-text="Your cart is reserved for {time} minutes."
 *   ></cart-expiry-timer>
 *
 * On every cart update (quantity change, add/remove) Shopify re-fetches the
 * cart-drawer section and morphs it into the DOM. The server-rendered
 * <cart-expiry-timer> tag is always empty (its text is 100% client-side),
 * so the morph strips this element's JS-injected content on every update —
 * this element itself isn't removed/reattached, just its children, so
 * connectedCallback never re-fires. Without the observer below, the text
 * stays blank until the next scheduled tick (up to 1s later). The
 * MutationObserver re-renders immediately instead of waiting on that.
 */
class CartExpiryTimer extends HTMLElement {
  static DURATION_SECONDS = 10 * 60;
  static STORAGE_KEY = 'vm-cart-expiry-end';

  #intervalId;
  #observer;

  connectedCallback() {
    this.#render();
    this.#intervalId = setInterval(() => this.#render(), 1000);

    this.#observer = new MutationObserver(() => {
      if (this.childNodes.length === 0) this.#render();
    });
    this.#observer.observe(this, { childList: true });
  }

  disconnectedCallback() {
    clearInterval(this.#intervalId);
    this.#observer?.disconnect();
  }

  #getEndTime(forceReset = false) {
    const stored = Number(sessionStorage.getItem(CartExpiryTimer.STORAGE_KEY));
    if (stored && !forceReset && stored > Date.now()) return stored;
    const end = Date.now() + CartExpiryTimer.DURATION_SECONDS * 1000;
    sessionStorage.setItem(CartExpiryTimer.STORAGE_KEY, String(end));
    return end;
  }

  #render() {
    let remainingMs = this.#getEndTime() - Date.now();
    // Hit zero — start a fresh countdown from the top instead of latching
    // to an expired state.
    if (remainingMs <= 0) {
      remainingMs = this.#getEndTime(true) - Date.now();
    }
    const remainingSeconds = Math.max(0, Math.round(remainingMs / 1000));

    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;
    const time = `${minutes}:${String(seconds).padStart(2, '0')}`;
    const template = this.dataset.activeText || '{time}';
    this.innerHTML = template.replace('{time}', `<strong>${time}</strong>`);
  }
}

if (!customElements.get('cart-expiry-timer')) {
  customElements.define('cart-expiry-timer', CartExpiryTimer);
}
